<template>
    <div>
      <div class="canvas">
        <small v-if="locked" class="locked-note">
          {{ lockedMessage }}
        </small>
        <canvas ref="canvas"
          @mousedown="mousedown"
          @mousemove="mousemove"
          @mouseup="sign = false"
          @mouseout="sign = false"
          @touchstart="touchstart"
          @touchmove="touchmove"
          @touchend="sign = false"
          @touchcancel="sign = false"
        />
      </div>

      <div class="actions-row">
        <slot />
        <b-button variant="dark" class="btn ovr-button__inverted" @click="clearSignature()">Clear</b-button>
      </div>

    </div>
</template>

<script>
export  default {
  name: "SignaturePad",
  emits : ['update:modelValue'],
  props: {
    modelValue: {
      type: String, // Base64 string
      default: ""
    },
    width:
    {
      type: Number,
      default:null
    },
    height:
    {
      type: Number,
      default:null
    },
    locked: {
      type: Boolean,
      default: false
    },
    lockedMessage: {
      type: String,
      default: "Uploaded images cannot be edited. Clear the signature to draw a new one."
    },
  },
  data() {
    return {
      canvas:null,
      ctx :  null,
      sign :  false,
      prevX :  0,
      prevY :  0,
      maxSignatureSize: 40 * 1024, // 40KB limit to match upload validation
    }
  },
  methods : {
    clearSignature()
    {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      // Emit an empty string to reset the signature
      this.$emit('update:modelValue', '');
    },
    mousedown($event)
    {
      if (this.locked) {
        return;
      }
      this.sign  =  true
      this.prevX  = $event.offsetX
      this.prevY  = $event.offsetY
    },
    mousemove($event)
    {
      if(this.sign)
      {
        const  currX  = $event.offsetX
        const  currY  = $event.offsetY
        this.draw(this.prevX, this.prevY, currX, currY)
        this.prevX  =  currX
        this.prevY  =  currY
      }
    },
    draw(depX, depY, destX, destY)
    {
      this.ctx.beginPath()
      this.ctx.moveTo(depX, depY)
      this.ctx.lineTo(destX, destY)
      this.ctx.closePath()
      this.ctx.stroke()

      // Use toBlob for better PNG compression, convert to data URL
      this.canvas.toBlob((blob) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const img = reader.result.replace('image/png', 'image/octet-stream')
          // Enforce 40KB limit to prevent database errors
          if (img.length > this.maxSignatureSize) {
            console.warn(`Signature size (${Math.round(img.length / 1024)}KB) exceeds 40KB limit. Consider drawing a simpler signature.`)
            return
          }
          this.$emit('update:modelValue', img)
        }
        reader.readAsDataURL(blob)
      }, 'image/png')
    },
    resizeCanvas() {
      if(this.width & this.height)
      {
        this.canvas.width = this.width;
        this.canvas.height = this.height;
      }
      else
      {
        const {width, height} = this.canvas.getBoundingClientRect();
        this.canvas.width = width;
        this.canvas.height = height;
      }
    },
    generateCanvas()
    {
      this.canvas = this.$el.getElementsByTagName('canvas')[0];

      this.resizeCanvas(); // Set initial size
      window.addEventListener("resize", this.resizeCanvas);

      this.ctx  = this.canvas.getContext('2d')
      this.ctx.strokeStyle  =  'black'
      this.ctx.lineWidth  =  2

      this.loadSignature();
    },
    loadSignature()
    {
      if (this.modelValue)
      {
        const img = new Image();
        img.src = this.modelValue;
        img.onload = () =>
        {
          this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
          this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
        };
      }
    },
    touchstart($event) {
      if (this.locked) {
        return;
      }
      if ($event.touches && $event.touches.length === 1) {
        const rect = this.canvas.getBoundingClientRect();
        const touch = $event.touches[0];
        this.sign = true;
        this.prevX = touch.clientX - rect.left;
        this.prevY = touch.clientY - rect.top;
      }
    },
    touchmove($event) {
      if (this.sign && $event.touches && $event.touches.length === 1) {
        $event.preventDefault(); // Prevent scrolling while drawing
        const rect = this.canvas.getBoundingClientRect();
        const touch = $event.touches[0];
        const currX = touch.clientX - rect.left;
        const currY = touch.clientY - rect.top;
        this.draw(this.prevX, this.prevY, currX, currY);
        this.prevX = currX;
        this.prevY = currY;
      }
    },
  },
  watch:
  {
    canvas()
    {
      if(this.canvas.width === 0)
      {
        this.generateCanvas();
      }
    },
    modelValue() {
      if (!this.ctx || !this.canvas) {
        return;
      }

      if (!this.modelValue) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        return;
      }

      this.loadSignature();
    }
  },
  mounted()
  {
    this.generateCanvas();
  },
  beforeUnmount()
  {
    window.removeEventListener("resize", this.resizeCanvas);
  }
}
</script>

<style scoped>
.canvas {
  position: relative;
  width:100%;
  height:180px;
  margin-bottom:15px;
  background-color: #fff;
  border: 1px solid #ced4da;
  cursor: crosshair;
  border-radius: .25rem;
}

canvas {
  width: 100%;
  height: 100%;
}

.locked-note {
  position: absolute;
  top: 8px;
  left: 12px;
  right: 12px;
  z-index: 1;
  color: #6c757d;
  background: rgba(255, 255, 255, 0.92);
}

.actions-row
{
  display:flex;
  justify-content: end;
  gap:15px;
}

.signature-upload-note {
  align-self: center;
  color: #6c757d;
}

</style>
